// Star shape strategy — 5-pointed star with top point as apex

import { registerShape } from '../services/ShapeRegistry';
import { findEdgeSideIntersection } from '../utils/geometryUtils';
import type { LineSeg } from '../utils/geometryUtils';
import type {
  ShapeDefinition,
  ShapeDrawSettings,
  EdgeData,
  Point,
  CollisionShape,
} from '../types/ShapeTypes';

const INNER_RADIUS_RATIO = 0.4;

function getVertices(cx: number, cy: number, w: number, h: number): Point[] {
  const outerRx = w / 2;
  const outerRy = h / 2;
  const innerRx = outerRx * INNER_RADIUS_RATIO;
  const innerRy = outerRy * INNER_RADIUS_RATIO;
  const vertices: Point[] = [];

  for (let i = 0; i < 5; i++) {
    // Outer point — starting from top (−90°), stepping 72°
    const outerAngle = ((i * 72 - 90) * Math.PI) / 180;
    vertices.push({
      x: cx + outerRx * Math.cos(outerAngle),
      y: cy + outerRy * Math.sin(outerAngle),
    });
    // Inner point — offset by 36°
    const innerAngle = (((i * 72 + 36) - 90) * Math.PI) / 180;
    vertices.push({
      x: cx + innerRx * Math.cos(innerAngle),
      y: cy + innerRy * Math.sin(innerAngle),
    });
  }

  return vertices;
}

function getStarSides(vertices: Point[]): LineSeg[] {
  const sides: LineSeg[] = [];
  const count = vertices.length;
  for (let i = 0; i < count; i++) {
    const next = (i + 1) % count;
    sides.push({
      x3: vertices[i].x, y3: vertices[i].y,
      x4: vertices[next].x, y4: vertices[next].y,
    });
  }
  return sides;
}

function drawPath(settings: ShapeDrawSettings): void {
  const { ctx, node, center: { x, y }, border, zoomLevel } = settings;
  const width = border ? node.width + node.borderWidth : node.width + node.borderWidth * 2;
  const height = border ? node.height + node.borderWidth : node.height + node.borderWidth * 2;
  const computedWidth = node.border ? width : node.width;
  const computedHeight = node.border ? height : node.height;

  const vertices = getVertices(x, y, computedWidth * zoomLevel, computedHeight * zoomLevel);
  ctx.moveTo(vertices[0].x, vertices[0].y);
  for (let i = 1; i < vertices.length; i++) {
    ctx.lineTo(vertices[i].x, vertices[i].y);
  }
  ctx.closePath();
}

function getEdgeConnectionPoint(edge: EdgeData, connected: 'from' | 'to'): Point {
  const line1 = { x1: edge.startPosX, y1: edge.startPosY, x2: edge.endPosX, y2: edge.endPosY };

  const isFrom = connected === 'from';
  const nodeWidth = isFrom
    ? edge.fromNodeWidth + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeWidth + edge.toNodeBorderWidthValue * 2;
  const nodeHeight = isFrom
    ? edge.fromNodeHeight + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeHeight + edge.toNodeBorderWidthValue * 2;
  const cx = isFrom ? edge.startPosX : edge.endPosX;
  const cy = isFrom ? edge.startPosY : edge.endPosY;

  const vertices = getVertices(cx, cy, nodeWidth, nodeHeight);
  const sides = getStarSides(vertices);
  return findEdgeSideIntersection(line1, sides);
}

function getCollisionData(edge: EdgeData, side: 'from' | 'to'): CollisionShape {
  const isFrom = side === 'from';
  const x = isFrom
    ? edge.startPosX - edge.fromNodeWidth / 2 - edge.fromNodeBorderWidthValue
    : edge.endPosX - edge.toNodeWidth / 2 - edge.toNodeBorderWidthValue;
  const y = isFrom
    ? edge.startPosY - edge.fromNodeHeight / 2 - edge.fromNodeBorderWidthValue
    : edge.endPosY - edge.toNodeHeight / 2 - edge.toNodeBorderWidthValue;
  const width = isFrom
    ? edge.fromNodeWidth + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeWidth + edge.toNodeBorderWidthValue * 2;
  const height = isFrom
    ? edge.fromNodeHeight + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeHeight + edge.toNodeBorderWidthValue * 2;

  return { type: 'rect', data: { x, y, width, height } };
}

const starShape: ShapeDefinition = {
  value: 'star',
  drawPath,
  getEdgeConnectionPoint,
  getCollisionData,
  resize: { constrainDimensions: false },
};

registerShape(starShape);

export { starShape };
